import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useEyeconxStore } from "../../../store/eyeconxStore";
import { ESVG } from "../../helpers/ESVG";

const sidebarItems = [
  {
    label: "Dashboard",
    icon: "/media/icons/duotune/general/gen008.svg",
    to: "/dashboard",
  },
  {
    label: "Appointments",
    icon: "/media/icons/duotune/general/gen066.svg",
    to: "/appointments",
  },
  {
    label: "Web Bookings",
    icon: "/media/icons/duotune/general/gen049.svg",
    to: "/web-bookings",
  },
  {
    label: "Patient Files",
    icon: "/media/icons/duotune/general/gen067.svg",
    to: "/patient-files",
  },
  { type: "divider", label: "Communication Tools" },
  {
    label: "Messages",
    icon: "/media/icons/duotune/communication/com007.svg",
    to: "/messages",
  },
  {
    label: "Surveys",
    icon: "/media/icons/duotune/general/gen068.svg",
    to: "/surveys",
  },
  {
    label: "Articles",
    icon: "/media/icons/duotune/general/gen005.svg",
    to: "/articles",
  },
  {
    label: "Newsletter",
    icon: "/media/icons/duotune/general/gen016.svg",
    submenuKey: "newsletter",
    submenu: [
      { label: "Email Newsletter", to: "/newsletter/email-newsletter" },
      { label: "SMS Newsletter", to: "/newsletter/sms-newsletter" },
    ],
  },
  {
    label: "Blast",
    icon: "/media/icons/duotune/communication/com014.svg",
    submenuKey: "blast",
    submenu: [
      { label: "Email Blast", to: "/blast/email" },
      { label: "SMS Blast", to: "/blast/sms" },
    ],
  },
  {
    label: "Reputation",
    icon: "/media/icons/duotune/general/gen003.svg",
    to: "/reputation",
  },
  { type: "divider", label: "Misc" },
  {
    label: "My Files",
    icon: "/media/icons/duotune/files/fil012.svg",
    to: "/my-files",
  },
  {
    label: "Settings",
    icon: "/media/icons/duotune/general/gen019.svg",
    submenuKey: "settings",
    submenu: [
      { label: "Clinic", to: "/settings/clinic" },
      { label: "Reminders", to: "/settings/reminders" },
      { label: "Users", to: "/settings/users"},
      { label: "Eyeconx Features", to: "./settings/features" },
      { label: "Templates", to: "/settings/templates" },
      { label: "Activity", to: "/settings/activity" },
    ],
  },
];

function EcxSidebar() {
  const sidebarCollapsed = useEyeconxStore((s) => s.sidebarCollapsed);
  const sidebarToggled = useEyeconxStore((s) => s.sidebarToggled);
  const sidebarBroken = useEyeconxStore((s) => s.sidebarBroken);
  const setSidebarCollapsed = useEyeconxStore((s) => s.setSidebarCollapsed);
  const toggleSidebar = useEyeconxStore((s) => s.toggleSidebar);
  const setSidebarBroken = useEyeconxStore((s) => s.setSidebarBroken);

  const [openSubmenus, setOpenSubmenus] = useState({});
  const [hoveredSubmenu, setHoveredSubmenu] = useState(null);

  // 🧭 Track position for floating submenu
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handleResize = () => setSidebarBroken(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setSidebarBroken]);

  const handleArrowClick = () => setSidebarCollapsed(!sidebarCollapsed);

  // 🧭 Updated to also capture position when collapsed
  const toggleSubmenu = (key, event) => {
    if (sidebarCollapsed) {
      const rect = event.currentTarget.getBoundingClientRect();
      setSubmenuPosition({ top: rect.top, left: rect.right });
      setHoveredSubmenu((prev) => (prev === key ? null : key));
    } else {
      setOpenSubmenus((prev) => ({ ...prev, [key]: !prev[key] }));
    }
  };

  const handleMenuItemHover = () => {
    if (sidebarCollapsed) setHoveredSubmenu(null);
  };

  // 🧭 Floating submenu popover
  const renderSubmenuTooltip = (key, items) =>
      sidebarCollapsed && hoveredSubmenu === key ? (
          <div
              className={`position-fixed bg-dark text-light rounded shadow-sm submenu-popover show`}
              style={{
                top: submenuPosition.top,
                left: submenuPosition.left + 4,
                zIndex: 1050,
                minWidth: "180px",
              }}
              onMouseEnter={() => setHoveredSubmenu(key)}
              onMouseLeave={() => setHoveredSubmenu(null)}
          >
            <ul className="list-unstyled m-0 p-2">
              {items.map((sub, idx) => (
                  <li key={idx}>
                    <Link
                        to={sub.to}
                        className="d-block text-decoration-none text-light py-1 px-2 rounded sidebar-sublink"
                        onClick={() => setHoveredSubmenu(null)}
                    >
                      {sub.label}
                    </Link>
                  </li>
              ))}
            </ul>
          </div>
      ) : null;

  return (
      <>
        {sidebarBroken && sidebarToggled && (
            <div
                className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
                style={{ zIndex: 1040 }}
                onClick={toggleSidebar}
            />
        )}

        <div
            className={`d-flex flex-column position-fixed top-0 start-0 h-100 bg-dark text-light transition-all
          ${sidebarCollapsed ? "sidebar-collapsed" : "sidebar-expanded"}
          ${sidebarBroken && !sidebarToggled ? "d-none" : ""}
          ${sidebarBroken ? "sidebar-mobile" : ""}`}
            style={{
              width: sidebarCollapsed ? "80px" : "280px",
              transition: "width 0.3s ease-in-out",
              zIndex: sidebarBroken ? 1050 : 1030,
              backgroundColor: "#1e1e2d",
            }}
        >
          {/* Header */}
          <div
              className="d-flex justify-content-between align-items-center px-3 mt-2 mb-3"
              style={{ height: 64 }}
          >
            {!sidebarCollapsed ? (
                <div className="bg-secondary bg-opacity-50 w-100 rounded p-2">
                  <img
                      src="/media/logos2/neweyeconxlogo.png"
                      className="w-100"
                      style={{ maxWidth: "100px" }}
                      alt="EyeconX"
                  />
                </div>
            ) : (
                <div
                    className="d-flex justify-content-center align-items-center bg-primary text-white rounded fw-bold"
                    style={{ width: 35, height: 35, fontSize: 24 }}
                >
                  E
                </div>
            )}
            <button
                className="btn btn-link text-light p-0 ms-2"
                onClick={handleArrowClick}
                style={{
                  transform: sidebarCollapsed ? "rotate(0deg)" : "rotate(180deg)",
                  transition: "transform 0.3s",
                }}
            >
              <ESVG path="/media/icons/duotune/arrows/arr021.svg" className="fs-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-fill px-2 overflow-hidden d-flex flex-column">
            <div className="overflow-y-auto flex-fill sidebar-scrollable">
              <ul className="list-unstyled mb-0">
                {sidebarItems.map((item, idx) => {
                  if (item.type === "divider") {
                    return (
                        <li className="my-3" key={idx}>
                          {sidebarCollapsed ? (
                              <div className="text-center">
                                <ESVG
                                    path="/media/icons/duotune/general/gen052.svg"
                                    className="text-muted"
                                />
                              </div>
                          ) : (
                              <small className="text-muted text-uppercase fw-medium px-3">
                                {item.label}
                              </small>
                          )}
                        </li>
                    );
                  }

                  const hasSubmenu = !!item.submenu;
                  return (
                      <li
                          key={idx}
                          className={`mb-1 position-relative ${hasSubmenu ? "has-submenu" : ""}`}
                      >
                        {hasSubmenu ? (
                            <>
                              <button
                                  className="btn btn-link d-flex align-items-center text-decoration-none text-light py-2 px-3 w-100 text-start sidebar-link"
                                  onClick={(e) => toggleSubmenu(item.submenuKey, e)} // 🧭 updated
                                  onMouseEnter={(e) => {
                                    if (sidebarCollapsed) {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setSubmenuPosition({
                                        top: rect.top,
                                        left: rect.right,
                                      });
                                      setHoveredSubmenu(item.submenuKey);
                                    }
                                  }}
                              >
                                <ESVG path={item.icon} size="md" className="me-2" />
                                {!sidebarCollapsed && (
                                    <>
                                      <span className="flex-fill">{item.label}</span>
                                      <i
                                          className={`bi ${
                                              openSubmenus[item.submenuKey]
                                                  ? "bi-chevron-up"
                                                  : "bi-chevron-down"
                                          } ms-auto`}
                                      />
                                    </>
                                )}
                              </button>
                              {renderSubmenuTooltip(item.submenuKey, item.submenu)}
                              {!sidebarCollapsed && (
                                  <div
                                      className={`collapse ${
                                          openSubmenus[item.submenuKey] ? "show" : ""
                                      }`}
                                  >
                                    <ul className="list-unstyled ms-4">
                                      {item.submenu.map((sub, sIdx) => (
                                          <li key={sIdx}>
                                            <Link
                                                to={sub.to}
                                                className="d-block text-decoration-none text-light py-1 px-3 rounded sidebar-sublink"
                                            >
                                              {sub.label}
                                            </Link>
                                          </li>
                                      ))}
                                    </ul>
                                  </div>
                              )}
                            </>
                        ) : (
                            <Link
                                to={item.to}
                                className="d-flex align-items-center text-decoration-none text-light py-2 px-3 rounded sidebar-link"
                                onMouseEnter={handleMenuItemHover}
                            >
                              <ESVG path={item.icon} size="md" className="me-2" />
                              {!sidebarCollapsed && <span>{item.label}</span>}
                            </Link>
                        )}
                      </li>
                  );
                })}
              </ul>
            </div>
          </nav>
        </div>

        {/* Style */}
        <style>{`
        .sidebar-link:hover {
          background-color: rgba(255, 255, 255, 0.1) !important;
        }
        .sidebar-sublink:hover {
          background-color: rgba(255, 255, 255, 0.05) !important;
        }
        .sidebar-collapsed { width: 80px !important; }
        .sidebar-expanded { width: 280px !important; }
        .sidebar-scrollable {
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
        }
        .sidebar-scrollable::-webkit-scrollbar { width: 6px; }
        .sidebar-scrollable::-webkit-scrollbar-thumb {
          background-color: rgba(255, 255, 255, 0.3);
          border-radius: 3px;
        }
        .sidebar-scrollable::-webkit-scrollbar-thumb:hover {
          background-color: rgba(255, 255, 255, 0.5);
        }
        @media (max-width: 768px) {
          .sidebar-mobile { width: 280px !important; }
        }

        /* 🧭 floating submenu animation */
        .submenu-popover {
          opacity: 0;
          transform: translateX(-5px);
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        .submenu-popover.show {
          opacity: 1;
          transform: translateX(0);
        }
      `}</style>
      </>
  );
}

export default EcxSidebar;
